/**
 * GLDN Gift Note — per-item gift option for Horizon cart
 *
 * Injects a "Gift, box separately" checkbox + To/From/Note form
 * inside each cart item row (as a grid-spanning cell). Saves gift
 * details as line item properties via /cart/change.js. Re-injects
 * after Horizon's morphSection updates.
 */
(function () {
  'use strict';

  const DELIMITER = ' -|- ';
  const MAX_NOTE_LENGTH = 60;

  /* Bounded retry for /cart/change.js. Under rapid cart mutation Shopify
   * throttles with 429/503 (often returning an HTML throttle page, not
   * JSON). The original save surfaced that as a one-shot console.error and
   * silently dropped the gift note — the field still showed the typed text,
   * so the customer never knew it didn't persist. Retry with backoff, then
   * surface a visible error. */
  const CART_CHANGE_MAX_RETRIES = 3;
  const CART_CHANGE_BACKOFF_MS = 400; // 400ms → 800ms → 1600ms

  /** POST /cart/change.js, retrying transient throttle responses. Resolves
   *  with the parsed cart JSON, or rejects after exhausting retries. */
  function postCartChange(body, attempt) {
    attempt = attempt || 0;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (res.ok) return res.json();
      var transient = res.status === 429 || res.status === 502 ||
                      res.status === 503 || res.status === 504;
      if (transient && attempt < CART_CHANGE_MAX_RETRIES) {
        var delay = CART_CHANGE_BACKOFF_MS * Math.pow(2, attempt);
        return new Promise(function (resolve) { setTimeout(resolve, delay); })
          .then(function () { return postCartChange(body, attempt + 1); });
      }
      throw new Error('Cart update failed: ' + res.status);
    });
  }

  /** Surface a save failure in the gift cell and keep the editor open so
   *  the typed note isn't visually lost. */
  function showSaveError(cell) {
    if (!cell) return;
    var form = cell.querySelector('.gldn-gift-form');
    if (form) { form.classList.add('open'); form.classList.remove('saved'); }
    var holder = cell.querySelector('.gldn-gift-fields') || cell;
    var err = cell.querySelector('.gldn-gift-save-error');
    if (!err) {
      err = document.createElement('div');
      err.className = 'gldn-gift-save-error';
      err.setAttribute('role', 'alert');
      holder.appendChild(err);
    }
    err.textContent = "Couldn't save your gift note — please try again.";
  }

  function clearSaveError(cell) {
    if (!cell) return;
    var err = cell.querySelector('.gldn-gift-save-error');
    if (err) err.remove();
  }

  /** Parse the joined "Gift To: X -|- Gift From: Y -|- Gift Note: Z" string */
  function parseGiftNote(raw) {
    const result = { to: '', from: '', note: '' };
    if (!raw) return result;
    const parts = raw.split(DELIMITER);
    for (const part of parts) {
      if (part.startsWith('Gift To:')) result.to = part.replace('Gift To:', '').trim();
      else if (part.startsWith('Gift From:')) result.from = part.replace('Gift From:', '').trim();
      else if (part.startsWith('Gift Note:')) result.note = part.replace('Gift Note:', '').trim();
    }
    return result;
  }

  /** Build the joined string from fields */
  function joinGiftNote(to, from, note) {
    const parts = [];
    if (to) parts.push('Gift To: ' + to);
    if (from) parts.push('Gift From: ' + from);
    if (note) parts.push('Gift Note: ' + note);
    return parts.join(DELIMITER);
  }

  /** Read existing gift properties from the DOM property list */
  function readGiftProps(row) {
    const props = row.querySelectorAll('.cart-items__property');
    let isGift = false;
    let giftNoteRaw = '';

    props.forEach(function (prop) {
      const dt = prop.querySelector('dt');
      const dd = prop.querySelector('dd');
      if (!dt || !dd) return;
      const key = dt.textContent.replace(':', '').trim();
      const val = dd.textContent.trim();

      if (key === 'Gift' && val === 'Yes') {
        isGift = true;
        dt.setAttribute('data-gldn-gift-prop', '');
      } else if (key === 'Gift Note') {
        giftNoteRaw = val;
        dt.setAttribute('data-gldn-gift-prop', '');
      }
    });

    return { isGift: isGift, parsed: parseGiftNote(giftNoteRaw) };
  }

  /** Create the gift note UI inside a single cart row */
  function createGiftUI(row) {
    /* Skip add-on / tag rows — they roll up under the parent builder
     * item and shouldn't carry their own gift-box checkbox or note. */
    if (row.classList.contains('cart-items__table-row--addon')) return;

    /* Skip gift card products — the gift card IS the gift, so a
     * separate "Add gift box & message" option doesn't apply. Flagged
     * server-side via data-gldn-gift-card on the row when
     * item.product.gift_card? is true. */
    if (row.getAttribute('data-gldn-gift-card') === 'true') return;

    /* Skip free / app-managed items — the free ring sizer and BOGOS freebies
     * ($0 lines) aren't things you gift-wrap, and doing so can race the
     * free-gift apps (gldn45 #334 QA H3). data-gldn-free = item.final_price==0. */
    if (row.getAttribute('data-gldn-free') === 'true' ||
        row.getAttribute('data-gldn-ring-sizer') === 'true') return;

    var key = row.getAttribute('data-key');
    if (!key) return;

    /* Skip if a gift cell for THIS line item key already exists anywhere
     * in the table (it may have been moved to the last addon row of the
     * builder set, so the row.querySelector below would miss it). */
    var existingCell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + key + '"]');
    if (existingCell) return;

    var data = readGiftProps(row);
    var uid = 'gldn-gift-' + key.replace(/[^a-zA-Z0-9]/g, '-');

    /* Label copy depends on whether the cart has 1 item or many:
     *   1 item  → "Add Gift Message" (single gift box is implicit)
     *   2+ items → "Add complimentary gift box & message"
     * Helper text removed per UAT. Counts main rows only (add-on rows
     * roll up under their builder parent). */
    var mainItemCount = document.querySelectorAll(
      '.cart-items__table-row:not(.cart-items__table-row--addon)'
    ).length;
    var labelText = mainItemCount > 1
      ? 'Add complimentary gift box &amp; message'
      : 'Add Gift Message';

    var cell = document.createElement('td');
    cell.className = 'gldn-gift-cell';
    cell.setAttribute('data-gldn-for-key', key);

    cell.innerHTML =
      '<div class="gldn-gift-check">' +
        '<label>' +
          '<input type="checkbox" class="gldn-gift-checkbox" id="' + uid + '"' +
            (data.isGift ? ' checked' : '') + '>' +
          '<span class="gldn-gift-label">' +
            '<span class="gldn-gift-label-main">' + labelText + '</span>' +
          '</span>' +
        '</label>' +
        '<button type="button" class="gldn-gift-edit"' +
          (data.isGift ? '' : ' style="display:none"') + '>Edit Gift Details</button>' +
      '</div>' +
      '<div class="gldn-gift-form' + (data.isGift ? ' open saved' : '') + '">' +
        '<div class="gldn-gift-fields">' +
          '<div class="gldn-gift-field">' +
            '<label for="' + uid + '-to">To:</label>' +
            '<input type="text" id="' + uid + '-to" class="gldn-gift-to" value="' + escapeAttr(data.parsed.to) + '"' + (data.isGift ? ' readonly' : '') + '>' +
          '</div>' +
          '<div class="gldn-gift-field">' +
            '<label for="' + uid + '-from">From:</label>' +
            '<input type="text" id="' + uid + '-from" class="gldn-gift-from" value="' + escapeAttr(data.parsed.from) + '"' + (data.isGift ? ' readonly' : '') + '>' +
          '</div>' +
          '<div class="gldn-gift-field gldn-gift-note-wrap">' +
            '<label for="' + uid + '-note">Gift Note (Optional):</label>' +
            '<textarea id="' + uid + '-note" class="gldn-gift-textarea" maxlength="' + MAX_NOTE_LENGTH + '">' +
              escapeHtml(data.parsed.note) +
            '</textarea>' +
            '<span class="gldn-gift-counter"><span class="gldn-gift-count">' +
              data.parsed.note.length + '</span>/' + MAX_NOTE_LENGTH +
            '</span>' +
          '</div>' +
          '<button type="button" class="gldn-gift-save">Save Gift Details</button>' +
        '</div>' +
      '</div>';

    /* For builder sets with add-ons, the gift checkbox should sit at the
     * bottom of the entire set (after the last add-on row), matching live.
     * Walk forward from the main row through siblings of the same builder
     * id and prefer the LAST one as the host. */
    var hostRow = row;
    var builderId = row.getAttribute('data-gldn-builder-id');
    if (builderId) {
      var sibling = row.nextElementSibling;
      while (sibling) {
        if (sibling.getAttribute && sibling.getAttribute('data-gldn-builder-id') === builderId) {
          hostRow = sibling;
        } else if (
          sibling.classList &&
          !sibling.classList.contains('cart-items__addons-header-row') &&
          !sibling.classList.contains('gldn-gift-row')
        ) {
          /* Hit a non-builder, non-header row — end of this set. */
          break;
        }
        sibling = sibling.nextElementSibling;
      }
    }

    // Insert BEFORE the Special Request cell if one exists, so the visual
    // grid order (both cells use grid-column: 1/-1) is: row → gift → request.
    var requestCell = hostRow.querySelector('.cart-items__request-cell');
    if (requestCell) {
      hostRow.insertBefore(cell, requestCell);
    } else {
      hostRow.appendChild(cell);
    }

    // Bind events
    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    var editBtn = cell.querySelector('.gldn-gift-edit');
    var form = cell.querySelector('.gldn-gift-form');
    var textarea = cell.querySelector('.gldn-gift-textarea');
    var counter = cell.querySelector('.gldn-gift-count');
    var saveBtn = cell.querySelector('.gldn-gift-save');

    /* To/From are read-only in the collapsed "saved" state so an edit can't be
       silently lost (it must go through "Edit Gift Details" → Save). Toggle the
       readonly flag alongside the saved/open state. */
    function setFieldsEditable(editable) {
      var to = cell.querySelector('.gldn-gift-to');
      var from = cell.querySelector('.gldn-gift-from');
      if (to) to.readOnly = !editable;
      if (from) from.readOnly = !editable;
    }

    checkbox.addEventListener('change', function () {
      if (checkbox.checked) {
        /* Newly checked → full editor open (no saved state yet). */
        form.classList.add('open');
        form.classList.remove('saved');
        editBtn.style.display = '';
        setFieldsEditable(true);
        var toInput = cell.querySelector('.gldn-gift-to');
        if (toInput) toInput.focus();
      } else {
        form.classList.remove('open');
        form.classList.remove('saved');
        editBtn.style.display = 'none';
        saveGiftDetails(cell, row, key);
      }
    });

    editBtn.addEventListener('click', function () {
      /* Edit always expands to full editor (To/From + Note + Save). */
      form.classList.add('open');
      form.classList.remove('saved');
      setFieldsEditable(true);
    });

    textarea.addEventListener('input', function () {
      if (textarea.value.length > MAX_NOTE_LENGTH) {
        textarea.value = textarea.value.substring(0, MAX_NOTE_LENGTH);
      }
      counter.textContent = textarea.value.length;
    });

    saveBtn.addEventListener('click', function () {
      /* Validate the To field before saving — gift box requires a
         recipient name. Outline the field red and show an inline
         error if empty. From is allowed to be blank at Save time
         (final To+From check runs at Checkout). */
      var toInput = cell.querySelector('.gldn-gift-to');
      if (!toInput || !toInput.value.trim()) {
        showFieldError(cell, '.gldn-gift-to', 'Recipient name is required.');
        if (toInput) toInput.focus();
        return;
      }
      clearFieldError(cell, '.gldn-gift-to');
      saveGiftDetails(cell, row, key);
      /* Collapse to "saved" state: To/From become read-only display, Gift Note
         + Save button hide. "Edit Gift Details" re-expands + re-enables. */
      form.classList.add('saved');
      setFieldsEditable(false);
    });
  }

  /** Show an inline error under a gift form field. Idempotent — re-uses
   *  the same error node if already present. */
  function showFieldError(scope, fieldSelector, message) {
    var field = scope.querySelector(fieldSelector);
    if (!field) return;
    field.classList.add('gldn-gift-field-invalid');
    var wrap = field.closest('.gldn-gift-field') || field.parentElement;
    if (!wrap) return;
    var err = wrap.querySelector('.gldn-gift-field-error');
    if (!err) {
      err = document.createElement('div');
      err.className = 'gldn-gift-field-error';
      wrap.appendChild(err);
    }
    err.textContent = message;
    /* Clear the error the next time the user edits the field. */
    if (!field._gldnErrorClearWired) {
      field.addEventListener('input', function () {
        if (field.value.trim()) clearFieldError(scope, fieldSelector);
      });
      field._gldnErrorClearWired = true;
    }
  }

  function clearFieldError(scope, fieldSelector) {
    var field = scope.querySelector(fieldSelector);
    if (!field) return;
    field.classList.remove('gldn-gift-field-invalid');
    var wrap = field.closest('.gldn-gift-field') || field.parentElement;
    if (!wrap) return;
    var err = wrap.querySelector('.gldn-gift-field-error');
    if (err) err.remove();
  }

  /** Save gift details via /cart/change.js
   *
   * /cart/change.js REPLACES the line item's properties hash — any key not
   * included in the request gets wiped. The old implementation collected
   * "existing" properties from .cart-items__property DOM elements, which
   * doesn't include internal/underscore-prefixed props or anything the
   * cart template filtered out (Builder, Preview, Customization, etc.).
   * That silently destroyed personalizer metadata on every gift save.
   *
   * Fetch the canonical line item from /cart.js so we preserve every
   * property the server knows about. Strip any http:// URL values — those
   * are images/uploads that Shopify re-resolves, and we should send them
   * as the original property value, not a mutated DOM text.
   */
  function saveGiftDetails(cell, row, itemKey) {
    // cell is passed directly by the caller (closure-captured in
    // createGiftUI). Do NOT re-query it from `row` — for a builder set
    // with add-ons, the gift cell is appended to the LAST add-on row
    // (hostRow), not the main `row`. row.querySelector('.gldn-gift-cell')
    // returns null there, which made Save Gift Details a silent no-op for
    // every personalized item that has an add-on (APEX 42a2ded5 follow-up:
    // "duplicating a product with an add-on clobbers the gift" — really
    // the gift never persisted in the first place). Fall back to a global
    // lookup by key if a caller ever omits the cell.
    if (!cell && itemKey) {
      cell = document.querySelector('.gldn-gift-cell[data-gldn-for-key="' + itemKey + '"]');
    }
    if (!cell) return;

    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    var isGift = checkbox.checked;
    var giftValue = isGift ? 'Yes' : '';
    var noteValue = '';

    if (isGift) {
      var to = cell.querySelector('.gldn-gift-to').value.trim();
      var from = cell.querySelector('.gldn-gift-from').value.trim();
      var note = cell.querySelector('.gldn-gift-textarea').value.trim();
      noteValue = joinGiftNote(to, from, note);
    }

    var saveBtn = cell.querySelector('.gldn-gift-save');
    if (saveBtn) saveBtn.disabled = true;
    clearSaveError(cell);

    // Snapshot all line-item keys sharing this variantId BEFORE the change.
    // After the save, the new key is whichever one isn't in this snapshot.
    // Variant-id-only matching breaks when Duplicate has produced a second
    // line with the same variant (APEX 42a2ded5).
    var variantId = itemKey.split(':')[0];
    var prevKeysForVariant = [];

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        prevKeysForVariant = (cart.items || [])
          .filter(function (i) { return i.key.split(':')[0] === variantId; })
          .map(function (i) { return i.key; });

        var item = (cart.items || []).find(function (i) { return i.key === itemKey; });
        // Stale-key self-heal: after a FIRST save (or a gift-box write) the line
        // key rotates, but this handler's `itemKey` was captured once at
        // createGiftUI time and is never reassigned. A second save (e.g. "Edit
        // Gift Details" → change recipient → Save) would post to a dead key —
        // Shopify no-ops it and the edit is silently lost on reload. Re-resolve
        // by the stable _unique / _random_id (data-gldn-unique on the row) and
        // write to the LIVE key instead. (gldn45 #334 QA — read-only-edit repro.)
        if (!item && row) {
          var uniq = row.getAttribute('data-gldn-unique');
          if (uniq) {
            item = (cart.items || []).find(function (i) {
              var p = i.properties || {};
              return (p._unique || p._random_id) === uniq;
            });
          }
          if (item) itemKey = item.key;
        }
        if (!item) {
          // The line genuinely isn't in the cart anymore — surface it (the
          // .catch below shows the save error + re-enables the button) rather
          // than posting to a dead key (a silent no-op).
          throw new Error('gift line no longer in cart: ' + itemKey);
        }
        var existingProps = item && item.properties ? Object.assign({}, item.properties) : {};
        // Preserve any gldn-gift-box "Add to box: N" segment — it rides inside
        // Gift Note but is owned by the box system; joinGiftNote() above rebuilds
        // the note without it, so re-attach or an owner loses its box grouping on
        // save (the checkout reconcile would re-add it, but this avoids the churn
        // and a visible flash). (gldn45 #334)
        var prevNote = existingProps['Gift Note'] || '';
        var boxSeg = '';
        prevNote.split(DELIMITER).forEach(function (p) {
          if (p.trim().indexOf('Add to box:') === 0) boxSeg = p.trim();
        });
        var finalNote = noteValue;
        if (boxSeg) finalNote = finalNote ? (finalNote + DELIMITER + boxSeg) : boxSeg;
        // Drop the two keys we're about to overwrite
        delete existingProps.Gift;
        delete existingProps['Gift Note'];

        var properties = Object.assign({}, existingProps, {
          Gift: giftValue,
          'Gift Note': finalNote
        });

        return postCartChange({ id: itemKey, properties: properties });
      })
      .then(function (cart) {
        clearSaveError(cell);
        var newKey = findNewKey(cart, itemKey, prevKeysForVariant);
        if (newKey && newKey !== itemKey) {
          cell.setAttribute('data-gldn-for-key', newKey);
          row.setAttribute('data-key', newKey);
        }
      })
      .catch(function (err) {
        console.error('Gift note save failed:', err);
        showSaveError(cell);
      })
      .finally(function () {
        if (saveBtn) saveBtn.disabled = false;
      });
  }

  /** After property change, the item key changes. Find the new one.
   *
   * `prevKeysForVariant` is the list of line-item keys sharing this
   * variantId BEFORE the change. The new key is whichever variant-matching
   * key isn't in that snapshot. Falls back to first-variant-match for
   * call sites that don't pass a snapshot (kept stable so existing callers
   * still work in the single-line-per-variant case). */
  function findNewKey(cart, oldKey, prevKeysForVariant) {
    if (!cart || !cart.items) return oldKey;
    var oldVariantId = oldKey.split(':')[0];
    var variantMatches = cart.items
      .map(function (i) { return i.key; })
      .filter(function (k) { return k.split(':')[0] === oldVariantId; });
    if (variantMatches.length === 0) return oldKey;
    if (Array.isArray(prevKeysForVariant) && prevKeysForVariant.length) {
      var fresh = variantMatches.find(function (k) {
        return prevKeysForVariant.indexOf(k) === -1;
      });
      if (fresh) return fresh;
    }
    return variantMatches[0];
  }

  /** Collect form state from all gift cells before a morph destroys them.
   *
   * Keyed by the full line-item key, NOT variantId. Two distinct cart
   * lines can share a variantId (Duplicate creates one) and keying by
   * variantId collapses them into one entry — the second cell's state
   * overwrites the first, then restoreFormState writes the merged state
   * into BOTH cells, producing the "typing on original applies to
   * duplicate" + "duplicate not typeable" bugs from APEX 42a2ded5. */
  function collectFormState() {
    var state = {};
    document.querySelectorAll('.gldn-gift-cell').forEach(function (cell) {
      var forKey = cell.getAttribute('data-gldn-for-key');
      if (!forKey) return;
      var f = cell.querySelector('.gldn-gift-form');
      state[forKey] = {
        checked: cell.querySelector('.gldn-gift-checkbox')?.checked || false,
        to: cell.querySelector('.gldn-gift-to')?.value || '',
        from: cell.querySelector('.gldn-gift-from')?.value || '',
        note: cell.querySelector('.gldn-gift-textarea')?.value || '',
        formOpen: f?.classList.contains('open') || false,
        // Editing = form open but not collapsed-to-saved (To/From editable).
        editing: !!(f && f.classList.contains('open') && !f.classList.contains('saved'))
      };
    });
    return state;
  }

  /** Restore form state after re-injection. Keyed by full line-item key
   *  to match collectFormState (see comment above). */
  function restoreFormState(state) {
    document.querySelectorAll('.gldn-gift-cell').forEach(function (cell) {
      var forKey = cell.getAttribute('data-gldn-for-key');
      if (!forKey) return;
      var saved = state[forKey];
      if (!saved) return;

      var checkbox = cell.querySelector('.gldn-gift-checkbox');
      var editBtn = cell.querySelector('.gldn-gift-edit');
      var form = cell.querySelector('.gldn-gift-form');
      var toInput = cell.querySelector('.gldn-gift-to');
      var fromInput = cell.querySelector('.gldn-gift-from');
      var textarea = cell.querySelector('.gldn-gift-textarea');
      var counter = cell.querySelector('.gldn-gift-count');

      // Change-only writes. Unconditionally re-setting counter.textContent (a
      // childList mutation) on every re-inject re-triggered the observeCartUpdates
      // MutationObserver, which re-scheduled another re-inject — a self-sustaining
      // reflow loop once any external mutation (e.g. gldn45 #334 gift-box row
      // grouping) kicked off the first re-inject. Only writing on an actual
      // change breaks the loop (gldn45 #334 review r3).
      if (checkbox && checkbox.checked !== saved.checked) checkbox.checked = saved.checked;
      if (toInput && toInput.value !== saved.to) toInput.value = saved.to;
      if (fromInput && fromInput.value !== saved.from) fromInput.value = saved.from;
      if (textarea) {
        if (textarea.value !== saved.note) textarea.value = saved.note;
        var _countStr = String(saved.note.length);
        if (counter && counter.textContent !== _countStr) counter.textContent = _countStr;
      }
      if (saved.checked && editBtn && editBtn.style.display !== '') editBtn.style.display = '';
      if (saved.formOpen && form && !form.classList.contains('open')) form.classList.add('open');
      // Restore the editing-vs-saved distinction so read-only survives a morph.
      if (form) {
        if (saved.editing) form.classList.remove('saved');
        var ro = !saved.editing;
        if (toInput && toInput.readOnly !== ro) toInput.readOnly = ro;
        if (fromInput && fromInput.readOnly !== ro) fromInput.readOnly = ro;
      }
    });
  }

  /** Hydrate every gift cell from the canonical /cart.js properties.
   *
   * The cart template filters out Gift / Gift Note from the visible
   * <dt>/<dd> property list (those are surfaced via the gift cell UI
   * instead). That means readGiftProps — which reads from the DOM —
   * never sees them on a fresh row, so freshly-injected cells for a
   * line that already has Gift=Yes saved server-side render unchecked.
   * This bit users hard after Duplicate: properties were copied to the
   * new line correctly, but the duplicated cell's checkbox + fields
   * showed empty (APEX 42a2ded5 follow-up).
   *
   * Fetch the canonical cart, find each cell's matching line, and
   * fill the checkbox + fields in place — but only if the cell isn't
   * already in user-input state (don't clobber an open form the user
   * is currently typing into).
   *
   * Coalesces concurrent requests via a single in-flight promise +
   * a short post-resolve cooldown — so the morph observer / reinject
   * loop can call this freely without exhausting the browser's
   * parallel-fetch budget on /cart.js. */
  var _hydrateInFlight = null;
  var _hydrateLastAt = 0;
  var _hydratePendingTimer = null;
  function hydrateGiftCellsFromCart() {
    if (_hydrateInFlight) return _hydrateInFlight;
    // Short cooldown — at most one /cart.js fetch per 750ms. If a request
    // arrives during cooldown, schedule a single deferred run for when the
    // cooldown expires so we don't drop "fire-after-duplicate" hydrate
    // requests that arrive right after init's first run.
    var sinceLast = Date.now() - _hydrateLastAt;
    if (sinceLast < 750) {
      if (!_hydratePendingTimer) {
        _hydratePendingTimer = setTimeout(function () {
          _hydratePendingTimer = null;
          hydrateGiftCellsFromCart();
        }, 750 - sinceLast + 50);
      }
      return null;
    }

    _hydrateInFlight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var byKey = {};
        (cart.items || []).forEach(function (i) { byKey[i.key] = i.properties || {}; });

        document.querySelectorAll('.gldn-gift-cell').forEach(function (cell) {
          var forKey = cell.getAttribute('data-gldn-for-key');
          var props = byKey[forKey];
          if (!props) return;
          if (props.Gift !== 'Yes') return;

          var checkbox = cell.querySelector('.gldn-gift-checkbox');
          if (!checkbox || checkbox.checked) return; // already shown / already in user-input state

          var parsed = parseGiftNote(props['Gift Note'] || '');
          var toInput = cell.querySelector('.gldn-gift-to');
          var fromInput = cell.querySelector('.gldn-gift-from');
          var textarea = cell.querySelector('.gldn-gift-textarea');
          var counter = cell.querySelector('.gldn-gift-count');
          var form = cell.querySelector('.gldn-gift-form');
          var editBtn = cell.querySelector('.gldn-gift-edit');

          checkbox.checked = true;
          if (toInput) toInput.value = parsed.to;
          if (fromInput) fromInput.value = parsed.from;
          if (textarea) {
            textarea.value = parsed.note;
            if (counter) counter.textContent = parsed.note.length;
          }
          if (form) form.classList.add('open', 'saved');
          if (editBtn) editBtn.style.display = '';
          // Hydrated cells land in the collapsed "saved" state → read-only.
          if (toInput) toInput.readOnly = true;
          if (fromInput) fromInput.readOnly = true;
        });
      })
      .catch(function () { /* leave cells as-is on network failure */ })
      .finally(function () {
        _hydrateInFlight = null;
        _hydrateLastAt = Date.now();
      });
    return _hydrateInFlight;
  }

  /** Inject gift UI into all cart item rows */
  function injectAll() {
    var rows = document.querySelectorAll('.cart-items__table-row[data-key]');
    rows.forEach(createGiftUI);
  }

  /** Intercept checkout to validate + save pending gift details */
  function interceptCheckout() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cart__checkout-button, #checkout, [name="checkout"]');
      if (!btn) return;

      var pendingCells = document.querySelectorAll('.gldn-gift-cell');
      if (pendingCells.length === 0) return;

      /* Collect gift-enabled cells (checkbox checked). Non-checked cells
         don't need validation or save. */
      var giftCells = [];
      pendingCells.forEach(function (cell) {
        if (cell.querySelector('.gldn-gift-checkbox')?.checked) {
          giftCells.push(cell);
        }
      });
      if (giftCells.length === 0) return;

      /* Validate To AND From for every gift-enabled cell. Build a list of
         offending line items so the toast can name them. Empty Gift Note
         is allowed (label says "Optional"). */
      var invalidItems = [];
      giftCells.forEach(function (cell) {
        var toInput = cell.querySelector('.gldn-gift-to');
        var fromInput = cell.querySelector('.gldn-gift-from');
        var toEmpty = !toInput || !toInput.value.trim();
        var fromEmpty = !fromInput || !fromInput.value.trim();
        if (toEmpty) showFieldError(cell, '.gldn-gift-to', 'Recipient name is required.');
        else clearFieldError(cell, '.gldn-gift-to');
        if (fromEmpty) showFieldError(cell, '.gldn-gift-from', 'Sender name is required.');
        else clearFieldError(cell, '.gldn-gift-from');
        if (toEmpty || fromEmpty) {
          var row = cell.closest('.cart-items__table-row');
          var title = row ? (row.querySelector('.cart-items__title')?.innerText?.trim() || 'this item') : 'this item';
          invalidItems.push({ title: title, cell: cell, missing: { to: toEmpty, from: fromEmpty } });
        }
      });

      if (invalidItems.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        /* Open the first offending gift form (so the user can see the
           highlighted field) and scroll it into view. */
        var first = invalidItems[0];
        var firstForm = first.cell.querySelector('.gldn-gift-form');
        if (firstForm) firstForm.classList.add('open');
        var firstField = first.cell.querySelector('.gldn-gift-to.gldn-gift-field-invalid')
                      || first.cell.querySelector('.gldn-gift-from.gldn-gift-field-invalid');
        if (firstField) {
          firstField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstField.focus({ preventScroll: true });
        }

        /* Toast naming all items missing details. */
        var msg;
        if (invalidItems.length === 1) {
          msg = 'Please complete the gift To/From fields for ' + invalidItems[0].title + ' before checkout.';
        } else {
          var titles = invalidItems.map(function (it) { return it.title; }).join(', ');
          msg = 'Please complete the gift To/From fields for: ' + titles;
        }
        showToast(msg);
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // Fetch the canonical cart state, then merge gift properties into
      // each affected line item before POSTing to /cart/change.js.
      //
      // The previous implementation sent only { Gift, "Gift Note" }, which
      // — because /cart/change.js REPLACES the line-item properties hash —
      // silently wiped every other property on every cart line (My Garden
      // flower personalization, personalizer Builder/Preview/Customization
      // metadata, the _unique de-dup key, etc.). It also iterated over ALL
      // pendingCells, so a single gift-wrapped item would clobber
      // personalization on every other (non-gift-wrapped) line too — the
      // no-op write below now prevents that.
      //
      // Promise.allSettled (not Promise.all) keeps a single failed save
      // from stranding the user on the cart page. Reaching checkout is
      // funnel-critical; a missing gift-note write is recoverable (user
      // can re-enter it later) but a stuck-on-cart dead-click is not. If
      // the /cart.js prefetch itself fails, skip the writes entirely
      // rather than risk a wipe — but still always redirect.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cart) {
          if (!cart) return [];
          var promises = [];
          pendingCells.forEach(function (cell) {
            var forKey = cell.getAttribute('data-gldn-for-key');
            if (!forKey) return;
            var p = saveGiftDetailsAsync(cell, forKey, cart);
            if (p) promises.push(p);
          });
          return Promise.allSettled(promises);
        })
        .then(function (results) {
          if (Array.isArray(results)) {
            var failures = results.filter(function (r) { return r.status === 'rejected'; });
            if (failures.length) {
              console.warn('gift-note save failures (proceeding to checkout anyway):', failures);
            }
          }
        })
        .catch(function (err) {
          console.warn('gift-note prefetch failed (proceeding to checkout anyway):', err);
        })
        // gldn45 #334: flush the per-line "Add to box: N" segment into each
        // boxed line's Gift Note FIRST (the value Tailor imports into Odoo), so
        // it's current even if the shopper never triggered the debounced
        // reconcile — and so the box numbers are persisted before the note is
        // built from them below. Rides this single interceptor so no competing
        // checkout listener is added (the duplicate-listener bug this file
        // documents). Both helpers swallow their own errors and never reject.
        //
        // gldn97 #393: syncIndicators ALSO dedupes gift messages before it
        // reconciles — the terminal guarantee that no box reaches fulfillment
        // with more than one message (order G754146 shipped with two). Silent
        // and non-blocking by design: the owner's message wins, the duplicate is
        // dropped, and the shopper is never prompted or held at the cart.
        .then(function () {
          return window.gldnGiftBoxSyncIndicators ? window.gldnGiftBoxSyncIndicators() : null;
        })
        // Then serialize the box summary into cart.note (order notes), reflecting
        // the now-persisted numbers.
        .then(function () {
          return window.gldnGiftBoxSerializeNote ? window.gldnGiftBoxSerializeNote() : null;
        })
        .catch(function () { /* box-note failure must not strand the user */ })
        .then(function () {
          window.location.href = '/checkout';
        });
    }, true);
  }

  /** Lightweight in-page toast. One instance at a time — re-uses the
   *  existing node if it's still onscreen. Auto-dismisses after 6s. */
  function showToast(message) {
    var existing = document.querySelector('.gldn-gift-toast');
    if (existing) existing.remove();
    var toast = document.createElement('div');
    toast.className = 'gldn-gift-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.textContent = message;
    document.body.appendChild(toast);
    /* Trigger CSS transition by adding the visible class on next frame. */
    requestAnimationFrame(function () {
      toast.classList.add('gldn-gift-toast--visible');
    });
    setTimeout(function () {
      toast.classList.remove('gldn-gift-toast--visible');
      setTimeout(function () { if (toast.parentNode) toast.remove(); }, 300);
    }, 6000);
  }

  /** Async version of saveGiftDetails for checkout interception.
   *
   * Takes a `cart` snapshot from /cart.js so we can MERGE existing line-
   * item properties before writing. /cart/change.js replaces the whole
   * properties hash — sending only { Gift, "Gift Note" } would wipe
   * personalization (Builder/Preview/Customization), required-request
   * answers (e.g. "*required* Let us know which flowers..."), and the
   * _unique de-dup key. The sync `saveGiftDetails` has done this merge
   * for a while; the async path was missing it (live bug: order #G724590
   * shipped without the My Garden flower property on May 19 2026).
   *
   * Returns null when there is nothing to write — the line is already in
   * the desired state. Callers skip non-gift cells entirely instead of
   * stamping `Gift: ""` onto items the user never gift-wrapped (which
   * would replace the properties hash and wipe their personalization).
   */
  function saveGiftDetailsAsync(cell, itemKey, cart) {
    var checkbox = cell.querySelector('.gldn-gift-checkbox');
    var isGift = checkbox && checkbox.checked;

    var item = (cart && cart.items || []).find(function (i) { return i.key === itemKey; });
    if (!item) return null; // line item no longer exists, nothing to write
    var existingProps = Object.assign({}, item.properties || {});
    var currentGift = existingProps.Gift || '';
    var currentNote = existingProps['Gift Note'] || '';

    // gldn45 #334: a box MEMBER (carries the `_gldn_box` grouping token, not its
    // own gift) is owned by gldn-gift-box — its Gift Note is just the "Add to
    // box: N" segment. NEVER rewrite it here: this checkout save used to clear it
    // (giftValue/noteValue = '') and drop the box grouping at checkout.
    if (existingProps['_gldn_box'] && !isGift) return null;

    // Preserve any gldn-gift-box "Add to box: N" segment on this line — it's
    // managed by the box system; joinGiftNote() below doesn't know about it, so
    // re-attach it or an owner would lose its box grouping at checkout (#334).
    var boxSeg = '';
    currentNote.split(DELIMITER).forEach(function (p) {
      if (p.trim().indexOf('Add to box:') === 0) boxSeg = p.trim();
    });

    var giftValue = isGift ? 'Yes' : '';
    var noteValue = '';
    if (isGift) {
      var to = cell.querySelector('.gldn-gift-to')?.value.trim() || '';
      var from = cell.querySelector('.gldn-gift-from')?.value.trim() || '';
      var note = cell.querySelector('.gldn-gift-textarea')?.value.trim() || '';
      noteValue = joinGiftNote(to, from, note);
      if (boxSeg) noteValue = noteValue ? (noteValue + DELIMITER + boxSeg) : boxSeg;
    }

    if (currentGift === giftValue && currentNote === noteValue) {
      return null;
    }

    delete existingProps.Gift;
    delete existingProps['Gift Note'];
    var properties = Object.assign({}, existingProps, {
      Gift: giftValue,
      'Gift Note': noteValue
    });

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: itemKey, properties: properties })
    }).then(function (res) { return res.json(); });
  }

  function escapeAttr(str) {
    return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeHtml(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  /** Watch for Horizon's morphSection updates and re-inject.
   *
   * Three layers of observation cover the morph patterns Horizon uses:
   *   1. tbody subtree (childList + subtree) — catches both row replacements
   *      AND in-row mutations where morph removes our injected gldn-gift-cell
   *      <td> while keeping the row itself. subtree:false would miss the
   *      latter (the row's children changed, not the tbody's).
   *   2. cart-items-component childList — catches when the entire tbody
   *      element gets swapped wholesale (morph in hydration mode sometimes
   *      replaces the whole cart-items-component contents).
   *   3. The body MutationObserver further down handles table appearance.
   *
   * Each observer feeds back into injectAll, which is idempotent (createGiftUI
   * bails on rows that already have a cell), so the multiple observers won't
   * cause duplicate cells. rAF debounces multiple mutations into one pass.
   */
  function observeCartUpdates() {
    var tbodies = document.querySelectorAll('.cart-items__table tbody');
    tbodies.forEach(function (tbody) {
      if (tbody._gldnGiftObserver) return;
      var observer = new MutationObserver(scheduleReinject);
      observer.observe(tbody, { childList: true, subtree: true });
      tbody._gldnGiftObserver = true;
    });

    var components = document.querySelectorAll('cart-items-component');
    components.forEach(function (comp) {
      if (comp._gldnGiftObserver) return;
      var observer = new MutationObserver(scheduleReinject);
      observer.observe(comp, { childList: true, subtree: true });
      comp._gldnGiftObserver = true;
    });
  }

  /* Debounced re-inject. Multiple mutations from a single morph batch into
   * one injectAll call on the next animation frame. Also prevents re-entry
   * during the inject itself (so our own DOM writes don't loop the observer). */
  var _reinjectScheduled = false;
  var _reinjectInProgress = false;
  function scheduleReinject() {
    if (_reinjectInProgress) return;
    if (_reinjectScheduled) return;
    _reinjectScheduled = true;
    requestAnimationFrame(function () {
      _reinjectScheduled = false;
      _reinjectInProgress = true;
      try {
        var state = collectFormState();
        injectAll();
        observeCartUpdates();
        restoreFormState(state);
        hydrateGiftCellsFromCart();
        // gldn45 #334: re-render gift-box controls + summary after a morph.
        if (window.gldnGiftBoxRender) window.gldnGiftBoxRender();
      } finally {
        _reinjectInProgress = false;
      }
    });
  }

  /** Initialize per-render concerns (gift-cell injection + cart-update observer).
   *
   * NOTE: `interceptCheckout()` is registered ONCE at the IIFE-level setup
   * below, NOT here. Previously it was called from `init()` which runs on
   * every cart-render — and `init()` is wired up via 4 retry timers
   * (initWithRetries), a `dialog:open` listener (with 3 more retries), a
   * cart-drawer-trigger click handler, and a body-level MutationObserver.
   * A normal cart drawer open/close cycle accumulated 8+ duplicate
   * document-level capture-phase listeners, each registered as a fresh
   * anonymous function (so they don't deduplicate). On the gift-checkbox-
   * checked path every duplicate fired its own `Promise.all` save chain
   * and its own `window.location.href = '/checkout'`, racing the others.
   * Lifting the registration out of init() makes it one-shot. */
  function init() {
    var tbodies = document.querySelectorAll('.cart-items__table tbody');
    if (!tbodies.length) return;

    injectAll();
    observeCartUpdates();
    hydrateGiftCellsFromCart();
    // gldn45 #334: let the gift-box grouping layer render its per-row controls
    // + summary into the freshly-injected gift cells (idempotent, no-op if the
    // script isn't present).
    if (window.gldnGiftBoxRender) window.gldnGiftBoxRender();
  }

  /** Register the checkout-button click interceptor exactly once per page
   * load (see the comment in init() above for the multiple-listener bug
   * this fix addresses). Safe to call before any cart is rendered — the
   * handler checks `pendingCells.length` at click time, not at register
   * time, so it no-ops on pages without gift cells. */
  interceptCheckout();

  // Expose so external scripts can re-run after swapping cart section HTML.
  // (Needed when the personalizer's addToCartSuccess replaces the cart-drawer
  // section innerHTML — the old tbody gets destroyed with its observer,
  // and the fresh tbody needs new injection + observer.)
  window.gldnGiftNoteInit = init;
  // Cheaper re-inject used from rAF / retry loops — just (re)walks rows
  // and creates gift cells. Safe to call repeatedly (createGiftUI bails
  // if the cell already exists).
  window.gldnGiftNoteRefresh = function () {
    try { injectAll(); observeCartUpdates(); } catch (e) { /* noop */ }
  };

  // Run when DOM is ready, then retry several times to cover edge cases
  // where init runs before Horizon's cart-items-component has finished
  // upgrading + connecting (a common race on fresh page loads where the
  // tbody exists in markup but the component lifecycle hasn't started
  // yet, so a downstream morph wipes our injected cells). Each retry is
  // idempotent — createGiftUI bails on rows that already have a cell.
  function initWithRetries() {
    init();
    setTimeout(init, 50);
    setTimeout(init, 250);
    setTimeout(init, 750);
    setTimeout(init, 1500);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithRetries);
  } else {
    initWithRetries();
  }
  // Also retry once the page finishes loading (resources + late-loading
  // modules like the cart-items-component custom element registration).
  window.addEventListener('load', function () {
    init();
    setTimeout(init, 100);
  });

  // Horizon's cart-items-component re-renders the cart section on every
  // cart:update event (morphSection wipes any JS-injected cells we had).
  // Re-hydrate the gift form after Horizon finishes — multiple scheduled
  // passes cover both the sync morph (when event.detail.data.sections is
  // set) and the async re-fetch morph (cache:false renderSection).
  // 1000ms backstop catches slow morphs on the very first cart add
  // from an empty cart (incognito / fresh session) where the drawer's
  // entire inner content swaps from empty-state to the full cart table.
  document.addEventListener('cart:update', function () {
    requestAnimationFrame(init);
    setTimeout(init, 150);
    setTimeout(init, 500);
    setTimeout(init, 1000);
  });

  // Also re-inject when cart drawer dialog opens
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-testid="cart-drawer-trigger"]')) {
      setTimeout(function () { injectAll(); observeCartUpdates(); }, 100);
    }
  });

  // Cart drawer auto-opens after add-to-cart via DialogOpenEvent — listen
  // for that too so we cover the first-add-from-empty-cart case where the
  // user never clicks the cart-drawer-trigger.
  document.addEventListener('dialog:open', function (e) {
    var t = e.target;
    if (!t || !(t.closest && t.closest('cart-drawer-component'))) return;
    requestAnimationFrame(init);
    setTimeout(init, 100);
    setTimeout(init, 300);
  });

  // Final safety net: MutationObserver on document.body that triggers init
  // the moment a cart-items table appears. Catches any morph timing edge
  // case the timed retries miss.
  if (typeof MutationObserver !== 'undefined') {
    var seenTable = !!document.querySelector('.cart-items__table');
    var bodyObserver = new MutationObserver(function () {
      var hasTable = !!document.querySelector('.cart-items__table');
      if (hasTable && !seenTable) {
        seenTable = true;
        requestAnimationFrame(init);
      } else if (!hasTable && seenTable) {
        // Cart was emptied — reset the flag so the next non-empty render
        // re-triggers init.
        seenTable = false;
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
